import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import TextureOutlinedIcon from "@mui/icons-material/TextureOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";

const STEP_COUNTS = {
  disenos: 4,
  searchs: 4,
  tile: 5,
  interpretacion: 5,
};

const TOPIC_META = {
  disenos: {
    accent: "#4338ca",
    Icon: DesignServicesOutlinedIcon,
  },
  searchs: {
    accent: "#0f766e",
    Icon: TravelExploreOutlinedIcon,
  },
  rapports: {
    accent: "#a16207",
    Icon: TextureOutlinedIcon,
  },
  tile: {
    accent: "#a16207",
    Icon: GridViewOutlinedIcon,
  },
  interpretacion: {
    accent: "#6d28d9",
    Icon: AutoFixHighOutlinedIcon,
  },
};

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const pop = keyframes`
  0%, 12% { opacity: 0.25; transform: translateY(6px) scale(0.9); }
  35%, 68% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0.35; transform: translateY(3px) scale(0.95); }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.55; }
  50% { transform: scale(1.08); opacity: 1; }
`;

const sweep = keyframes`
  0% { transform: translateX(-8px); opacity: 0.2; }
  45% { opacity: 1; }
  100% { transform: translateX(28px); opacity: 0.15; }
`;

const tileShift = keyframes`
  0%, 20% { transform: translate(0, 0); }
  50%, 70% { transform: translate(10px, 8px); }
  100% { transform: translate(0, 0); }
`;

const wandGlow = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.9) rotate(-8deg); }
  50% { opacity: 1; transform: scale(1.05) rotate(0deg); }
`;

function MiniStage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: 88,
        height: 64,
        flexShrink: 0,
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: tint,
        border: "1px solid",
        borderColor: alpha("#000", 0.06),
      }}
    >
      {children}
    </Box>
  );
}

function IconPreview({ color, Icon, anim = "pop" }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          color,
          animation:
            anim === "bob"
              ? `${bob} 2.2s ease-in-out infinite`
              : anim === "pulse"
                ? `${pulse} 2.4s ease-in-out infinite`
                : `${pop} 3.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <Icon sx={{ fontSize: 28 }} />
      </Box>
    </MiniStage>
  );
}

function SearchRunPreview({ color }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      <Box
        sx={{
          position: "absolute",
          left: 10,
          top: 12,
          width: 40,
          height: 28,
          borderRadius: 1,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(color, 0.4),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 14,
          top: 18,
          width: 18,
          height: 3,
          borderRadius: 1,
          bgcolor: alpha(color, 0.45),
          animation: `${sweep} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 12,
          top: 18,
          width: 22,
          height: 22,
          borderRadius: "50%",
          bgcolor: alpha(color, 0.2),
          display: "grid",
          placeItems: "center",
          animation: `${pulse} 1.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <PlayArrowOutlinedIcon sx={{ fontSize: 14, color }} />
      </Box>
    </MiniStage>
  );
}

function PhotosPreview({ color }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 12 + i * 14,
            top: 14 + i * 4,
            width: 26,
            height: 32,
            borderRadius: 0.75,
            bgcolor: "#fff",
            border: "1.5px solid",
            borderColor: alpha(color, 0.4),
            animation: `${pop} 3.4s ease-in-out infinite`,
            animationDelay: `${i * 0.18}s`,
            ...motionSafe,
          }}
        />
      ))}
    </MiniStage>
  );
}

function NoDupPreview({ color }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      <Box
        sx={{
          position: "absolute",
          left: 18,
          top: 14,
          width: 28,
          height: 34,
          borderRadius: 1,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(color, 0.4),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 14,
          bottom: 10,
          width: 22,
          height: 22,
          borderRadius: "50%",
          bgcolor: alpha(color, 0.18),
          display: "grid",
          placeItems: "center",
          animation: `${bob} 2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <BlockOutlinedIcon sx={{ fontSize: 14, color }} />
      </Box>
    </MiniStage>
  );
}

function TileMiniPreview({ color }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      <Box
        sx={{
          position: "absolute",
          left: 14,
          top: 12,
          width: 28,
          height: 28,
          borderRadius: 0.5,
          bgcolor: alpha(color, 0.35),
          border: "1px solid",
          borderColor: color,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 14,
          top: 12,
          width: 28,
          height: 28,
          borderRadius: 0.5,
          bgcolor: alpha(color, 0.22),
          border: "1px solid",
          borderColor: alpha(color, 0.55),
          animation: `${tileShift} 2.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </MiniStage>
  );
}

function InterpretMiniPreview({ color }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      <Box
        sx={{
          position: "absolute",
          left: 12,
          top: 14,
          width: 36,
          height: 36,
          borderRadius: 1,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(color, 0.4),
          backgroundImage: `
            radial-gradient(circle at 30% 35%, ${alpha(color, 0.35)} 0 6px, transparent 7px),
            radial-gradient(circle at 70% 65%, ${alpha(color, 0.28)} 0 7px, transparent 8px)
          `,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 10,
          top: 12,
          color,
          animation: `${wandGlow} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <AutoFixHighOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>
    </MiniStage>
  );
}

function StepCard({ Preview, title, body, color, delayMs, stepIndex }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.35,
        p: 1.35,
        borderRadius: 2.5,
        border: "1.5px solid",
        borderColor: alpha(color, 0.28),
        bgcolor: alpha(color, 0.06),
        boxShadow: `0 4px 14px ${alpha(color, 0.08)}`,
        animation: `${rise} 320ms ease both`,
        animationDelay: `${delayMs}ms`,
        ...motionSafe,
      }}
    >
      <Preview color={color} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {title ? (
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, lineHeight: 1.25, mb: 0.4, color }}
          >
            {title}
          </Typography>
        ) : stepIndex != null ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 22,
              height: 22,
              borderRadius: "50%",
              bgcolor: alpha(color, 0.16),
              color,
              fontSize: "0.72rem",
              fontWeight: 800,
              mb: 0.55,
            }}
          >
            {stepIndex}
          </Box>
        ) : null}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.45 }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

function stepsOf(topic, t) {
  const n = STEP_COUNTS[topic] || 0;
  return Array.from({ length: n }, (_, i) =>
    t(`skillAgent.usageHelp.${topic}.step${i + 1}`),
  );
}

function previewsFor(topic) {
  if (topic === "disenos") {
    return [
      (p) => <IconPreview {...p} Icon={AddOutlinedIcon} />,
      (p) => <IconPreview {...p} Icon={EditNoteOutlinedIcon} anim="bob" />,
      (p) => <IconPreview {...p} Icon={ContentCopyOutlinedIcon} />,
      (p) => (
        <IconPreview
          {...p}
          Icon={CheckCircleOutlineOutlinedIcon}
          anim="pulse"
        />
      ),
    ];
  }
  if (topic === "searchs") {
    return [
      (p) => <IconPreview {...p} Icon={AddOutlinedIcon} />,
      (p) => <SearchRunPreview {...p} />,
      (p) => <PhotosPreview {...p} />,
      (p) => <NoDupPreview {...p} />,
    ];
  }
  return Array.from({ length: STEP_COUNTS[topic] || 0 }, () => (p) => (
    <IconPreview {...p} Icon={HelpOutlineOutlinedIcon} />
  ));
}

export default function SkillAgentSectionHelpDialog({ open, topic, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const key = STEP_COUNTS[topic]
    ? topic
    : topic === "rapports"
      ? "rapports"
      : "";
  if (!key) return null;

  const meta = TOPIC_META[key] || TOPIC_META.disenos;
  const accent = meta.accent;
  const HeaderIcon = meta.Icon;
  const steps = key === "rapports" ? [] : stepsOf(key, t);
  const previews = key === "rapports" ? [] : previewsFor(key);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      keepMounted={false}
      transitionDuration={160}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          maxHeight: "min(92vh, 860px)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(
            theme.palette.secondary?.main || accent,
            0.07,
          )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.16),
                color: accent,
                flexShrink: 0,
              }}
            >
              <HeaderIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {t(`skillAgent.usageHelp.${key}.title`)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
              >
                {t(`skillAgent.usageHelp.${key}.lead`)}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={t("skillAgent.usageHelp.close")}
            onClick={onClose}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 2.5,
          pt: 1.75,
          pb: 1,
          overflow: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        {key === "rapports" ? (
          <Stack spacing={1.2}>
            {[
              {
                id: "tile",
                color: "#a16207",
                Preview: TileMiniPreview,
              },
              {
                id: "interpret",
                color: "#6d28d9",
                Preview: InterpretMiniPreview,
              },
            ].map(({ id, color, Preview }, i) => (
              <StepCard
                key={id}
                Preview={Preview}
                title={t(`skillAgent.usageHelp.rapports.${id}Title`)}
                body={t(`skillAgent.usageHelp.rapports.${id}Body`)}
                color={color}
                delayMs={40 + i * 55}
              />
            ))}
          </Stack>
        ) : (
          <Stack spacing={1.2}>
            {steps.map((text, i) => {
              const Preview = previews[i] || previews[0];
              return (
                <StepCard
                  key={`${key}-${i}`}
                  Preview={Preview}
                  body={text}
                  color={accent}
                  delayMs={40 + i * 45}
                  stepIndex={i + 1}
                />
              );
            })}
          </Stack>
        )}
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            minHeight: 42,
            borderRadius: 2,
            fontWeight: 800,
            px: 2.5,
            bgcolor: accent,
            "&:hover": { bgcolor: accent, filter: "brightness(0.92)" },
          }}
        >
          {t("skillAgent.usageHelp.close")}
        </Button>
      </Stack>
    </Dialog>
  );
}
